import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { object, number, string, array, InferType, boolean } from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Button,
    Card,
    CardBody,
    Switch,
    Listbox,
    ListboxItem,
    ListboxSection,
    Selection,
    Input,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faXmark,
    faClone,
    faPenToSquare,
    faCheckCircle,
    faBookmark,
} from "@fortawesome/free-solid-svg-icons";
import allNHorizontalImage from "@/assets/images/all_n_horizontal.png";
import { useMemo, useState, useEffect, useCallback } from "react";
import { Fertilizer, FertilizerType } from "@/types/fertilizer";
import { ApplicationMethod } from "@/types/enums";
import {
    ApplicationPlan,
    PlantingPlan,
    SimulatedCropPlan,
} from "@/types/cropPlan";
import { UseQueryResult } from "@tanstack/react-query";
import AutoCompleteInput from "../../inputs/AutoCompleteInput";
import NumberInput from "../../inputs/NumberInput";
import SelectInput from "../../inputs/SelectInput";
import SectionedAutoCompleteInput from "../../inputs/SectionedAutoComplete";
import TextInput from "@/components/inputs/TextInput";
import { ConfirmPrimaryPlan } from "./ConfirmPlan";

const SENTINEL_ORGANIZATION_ID = 1;

const applicationMethodOptions = [
    {
        id: ApplicationMethod.InjectedKnifedCoultered.toString(),
        description: "Injected/Knifed/Coultered",
    },
    { id: ApplicationMethod.Dribble.toString(), description: "Dribble" },
    { id: ApplicationMethod.Broadcast.toString(), description: "Broadcast" },
    { id: ApplicationMethod.Topdress.toString(), description: "Topdress" },
    { id: ApplicationMethod.YDrop.toString(), description: "Y-Drop" },
    {
        id: ApplicationMethod.BroadcastTillage.toString(),
        description: "Broadcast Tillage",
    },
    { id: ApplicationMethod.TwoXTwo.toString(), description: "2x2" },
    { id: ApplicationMethod.Furrow.toString(), description: "Furrow" },
    {
        id: ApplicationMethod.Fertigation.toString(),
        description: "Fertigation",
    },
];

const applicationPlanSchema = object({
    id: number().nullable(),
    name: string().required(),
    organization: number().required(),
    planned_applications: array()
        .of(
            object({
                id: number().nullable(),
                target_crop_stage: number().required(),
                method: string()
                    .oneOf([
                        ApplicationMethod.InjectedKnifedCoultered,
                        ApplicationMethod.Dribble,
                        ApplicationMethod.Broadcast,
                        ApplicationMethod.Topdress,
                        ApplicationMethod.YDrop,
                        ApplicationMethod.BroadcastTillage,
                        ApplicationMethod.TwoXTwo,
                        ApplicationMethod.Furrow,
                        ApplicationMethod.Fertigation,
                    ])
                    .required(),
                user_rate: number().nullable(),
                application_cost: number().nullable(),
                fertilizer_product: string().required(),
                model_rate: number().nullable(),
                establish_plots: boolean().required(),
            }),
        )
        .required(),
});

type ApplicationPlanFormValues = InferType<typeof applicationPlanSchema>;

export function ApplicationPlanForm({
    fields,
    organizations,
    selectedApplicationPlanIds,
    setSelectedApplicationPlanIds,
    applicationPlansRequest,
    cropStageOptions,
    plantingPlan,
    cropPlansRequest,
    selectedSimulations,
    selectionMode = "multiple",
    setPrimaryPlan = true,
}: {
    fields: { id: number; active_planting_operation: { id: number } | null }[];
    organizations: { id: number; description: string }[];
    selectedApplicationPlanIds: Selection;
    setSelectedApplicationPlanIds: (selection: Selection) => void;
    applicationPlansRequest: UseQueryResult<ApplicationPlan[]>;
    cropStageOptions: { id: number; description: string }[];
    plantingPlan: PlantingPlan;
    cropPlansRequest: UseQueryResult<SimulatedCropPlan[]>;
    selectedSimulations: Selection;
    selectionMode?: "single" | "multiple";
    setPrimaryPlan?: boolean;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();

    const { user, userIsSentinel, isGrower } = useAuth();

    const userOrganizationId: number | undefined = useMemo(() => {
        if (typeof user?.organization === "object") {
            return user.organization.id;
        }
        return typeof user?.organization === "string"
            ? parseInt(user.organization)
            : user?.organization;
    }, [user]);

    const applicationPlanForm = useForm<ApplicationPlanFormValues>({
        resolver: yupResolver(applicationPlanSchema),
        defaultValues: {
            id: null,
            organization: userOrganizationId,
            planned_applications: [
                {
                    id: null,
                    method: ApplicationMethod.InjectedKnifedCoultered,
                    fertilizer_product: "2",
                    user_rate: null,
                    application_cost: null,
                    establish_plots: false,
                },
            ],
        },
    });

    const [
        applicationPlanId,
        applicationPlanOrganization,
        planned_applications,
    ] = applicationPlanForm.watch([
        "id",
        "organization",
        "planned_applications",
    ]);

    const userCanEditApplicationPlan = useMemo(() => {
        return (
            userIsSentinel ||
            (!isGrower &&
                applicationPlanOrganization !== SENTINEL_ORGANIZATION_ID) ||
            !applicationPlanOrganization ||
            applicationPlanOrganization === userOrganizationId
        );
    }, [
        applicationPlanOrganization,
        userOrganizationId,
        userIsSentinel,
        isGrower,
    ]);

    const fertilizersQuery = useGetRequest<Fertilizer[]>({
        url: `/fertilizers/`,
    });

    const fertilizerOptions = useMemo(() => {
        const dryFertilizers =
            fertilizersQuery.data
                ?.filter(
                    (fertilizer) =>
                        fertilizer.fertilizer_type === FertilizerType.dry,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        const liquidFertilizers =
            fertilizersQuery.data
                ?.filter(
                    (fertilizer) =>
                        fertilizer.fertilizer_type === FertilizerType.liquid,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        return [
            {
                value: FertilizerType.dry,
                label: "Dry Fertilizers",
                options: dryFertilizers,
            },
            {
                value: FertilizerType.liquid,
                label: "Liquid Fertilizers",
                options: liquidFertilizers,
            },
        ];
    }, [fertilizersQuery.data]);

    const [editApplicationPlan, setEditApplicationPlan] =
        useState<boolean>(true);

    const existingApplicationPlanOrganizations = useMemo(() => {
        const orgIds = new Set<number>([SENTINEL_ORGANIZATION_ID]);
        if (applicationPlansRequest.data) {
            applicationPlansRequest.data.forEach((plan) => {
                orgIds.add(plan.organization);
            });
        }
        return (
            organizations.some((org) => org.id === SENTINEL_ORGANIZATION_ID) ?
                organizations.filter((org) => orgIds.has(org.id)) ?? [] :
                (organizations.filter((org) => orgIds.has(org.id)) ?? []).concat(
                    {
                        id: SENTINEL_ORGANIZATION_ID,
                        description: "Sentinel",
                    }
                )
        );
    }, [applicationPlansRequest.data, organizations]);

    const applicationPlansOptions = useMemo(() => {
        if (!applicationPlansRequest.data) {
            return [];
        }
        return applicationPlansRequest.data.map((plan) => ({
            ...plan,
            group: plan.organization.toString(),
        }));
    }, [applicationPlansRequest.data]);

    const primaryCropPlan = useMemo(() => {
        if (!cropPlansRequest.data || fields.length > 1) {
            return null;
        }
        return cropPlansRequest.data.find((plan) => plan.is_primary);
    }, [cropPlansRequest.data, fields]);

    const applicationPlan = useMemo(() => {
        return applicationPlansOptions.find(
            (plan) => plan.id === applicationPlanId
        );
    }, [applicationPlansOptions, applicationPlanId]);

    const saveApplicationPlanMutation = useMutateRequest<ApplicationPlan>({
        successMessage: "Application Plan Saved",
        onSuccess: () => {
            setEditApplicationPlan(false);
            applicationPlansRequest.refetch();
        },
    });

    useEffect(() => {
        const selectedKeys = Array.from(selectedApplicationPlanIds);
        const displayKey = selectedKeys[selectedKeys.length - 1];
        const applicationPlan = applicationPlansOptions.find(
            (plan) =>
                (plan.id && plan.id.toString() === displayKey) ||
                (plan.id === null && displayKey === "null"),
        );
        if (applicationPlan) {
            const simulatedPlan = cropPlansRequest.data?.find(
                (plan) =>
                    plan.application_plan?.id === applicationPlan.id &&
                    plan.simulation_type === Array.from(selectedSimulations)[0],
            );
            applicationPlanForm.reset({
                id: applicationPlan.id,
                name: applicationPlan?.name || "",
                organization:
                    applicationPlan.organization || userOrganizationId,
                planned_applications:
                    applicationPlan.planned_applications.length > 0
                        ? applicationPlan.planned_applications.map((app) => ({
                            id: app.id,
                            target_crop_stage: app.target_crop_stage,
                            method: app.method,
                            fertilizer_product:
                                app.fertilizer_product.toString(),
                            user_rate: app.user_rate,
                            application_cost: app.application_cost,
                            establish_plots: app.establish_plots,
                            model_rate:
                                simulatedPlan?.simulated_rates?.find(
                                    (rate) =>
                                        rate.planned_application === app.id,
                                )?.optimal_rate || null,
                        }))
                        : [
                            {
                                id: null,
                                method: ApplicationMethod.InjectedKnifedCoultered,
                                fertilizer_product: "2",
                                user_rate: null,
                                application_cost: null,
                                establish_plots: false,
                                model_rate: null,
                            },
                        ],
            });
        } else {
            applicationPlanForm.reset({
                id: null,
                name: undefined,
                organization: userOrganizationId,
                planned_applications: [
                    {
                        id: null,
                        method: ApplicationMethod.InjectedKnifedCoultered,
                        fertilizer_product: "2",
                        user_rate: null,
                        application_cost: null,
                        establish_plots: false,
                    },
                ],
            });
        }
    }, [
        selectedApplicationPlanIds,
        applicationPlansOptions,
        applicationPlanForm,
        userOrganizationId,
        cropPlansRequest.data,
        selectedSimulations,
    ]);

    const handleSaveApplicationPlan = useCallback(() => {
        const applicationData = {
            id: applicationPlanId,
            organization: applicationPlanForm.getValues("organization"),
            name: applicationPlanForm.getValues("name"),
            planned_applications: applicationPlanForm
                .getValues("planned_applications")
                .map((application) => ({
                    id: application.id,
                    target_crop_stage: application.target_crop_stage,
                    method: application.method,
                    user_rate: Number(application.user_rate) || null,
                    application_cost: application.application_cost,
                    fertilizer_product: application.fertilizer_product,
                    establish_plots: application.establish_plots,
                })),
        };
        if (applicationPlanId) {
            saveApplicationPlanMutation.mutate({
                url: `/application_plans/${applicationPlanId}/`,
                method: "PUT",
                data: applicationData,
            });
            return;
        } else {
            saveApplicationPlanMutation.mutate({
                url: `/application_plans/`,
                method: "POST",
                data: applicationData,
            });
            return;
        }
    }, [applicationPlanForm, saveApplicationPlanMutation, applicationPlanId]);

    useEffect(() => {
        if (Array.from(selectedApplicationPlanIds).length > 0) {
            setEditApplicationPlan(false);
        }
    }, [selectedApplicationPlanIds]);

    const totalNitrogenApplied = planned_applications.reduce(
        (total, application) => {
            return total + (Number(application.user_rate) || 0);
        },
        0,
    );

    return (
        <>
            <div className="font-bold text-2xl mb-2 text-primary-500">
                Applications
            </div>
            <div className="flex flex-row gap-2 w-full">
                <div className="flex flex-col w-1/4 h-full gap-3">
                    <Listbox
                        items={applicationPlansOptions}
                        selectionMode={selectionMode}
                        color="primary"
                        variant="light"
                        className="border border-gray-300 rounded"
                        emptyContent="No Plans Found"
                        selectedKeys={selectedApplicationPlanIds}
                        onSelectionChange={setSelectedApplicationPlanIds}
                    >
                        {existingApplicationPlanOrganizations.map((org) => (
                            <ListboxSection
                                title={org.description}
                                key={org.id}
                                showDivider={true}
                                className="font-bold"
                            >
                                {applicationPlansOptions
                                    .filter(
                                        (plan) =>
                                            plan.group === org.id.toString(),
                                    )
                                    .map((plan) => (
                                        <ListboxItem
                                            key={plan.id!}
                                            className={
                                                applicationPlanId === plan.id
                                                    ? "bg-primary-500 bg-opacity-20 text-primary-700"
                                                    : ""
                                            }
                                            startContent={
                                                org.id ===
                                                SENTINEL_ORGANIZATION_ID && (
                                                    <img
                                                        src={
                                                            allNHorizontalImage
                                                        }
                                                        alt="All N Horizontal"
                                                        className="h-5 object-cover"
                                                    />
                                                )
                                            }
                                            endContent={
                                                plan.id ===
                                                primaryCropPlan
                                                    ?.application_plan
                                                    ?.id && (
                                                    <FontAwesomeIcon
                                                        icon={faBookmark}
                                                        className="text-primary-500"
                                                    />
                                                )
                                            }
                                        >
                                            {plan.name}
                                        </ListboxItem>
                                    ))}
                            </ListboxSection>
                        ))}
                    </Listbox>
                    <Button
                        size="sm"
                        color="primary"
                        onPress={() => {
                            applicationPlanForm.reset({
                                id: null,
                                organization: userOrganizationId,
                                name: "New Application Plan",
                                planned_applications: [
                                    {
                                        id: null,
                                        method: ApplicationMethod.InjectedKnifedCoultered,
                                        fertilizer_product: "2",
                                        user_rate: null,
                                        application_cost: null,
                                        establish_plots: false,
                                    },
                                ],
                            });
                            setEditApplicationPlan(true);
                        }}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        New Application Plan
                    </Button>
                </div>
                <div className="flex flex-col w-4/5">
                    <FormProvider {...applicationPlanForm}>
                        <form>
                            <div className="flex flex-row justify-between items-center gap-2 w-full">
                                <div className="flex flex-col w-full">
                                    <TextInput
                                        name="name"
                                        label="Plan Name"
                                        labelPlacement="inside"
                                        size="sm"
                                        isReadOnly={
                                            !userCanEditApplicationPlan ||
                                            !editApplicationPlan
                                        }
                                    />
                                </div>
                                {!isGrower && (
                                    <div className="flex flex-col w-full">
                                        <AutoCompleteInput
                                            name="organization"
                                            label="Organization"
                                            labelPlacement="inside"
                                            options={organizations}
                                            size="sm"
                                            isReadOnly={
                                                !userCanEditApplicationPlan ||
                                                !editApplicationPlan
                                            }
                                        />
                                    </div>
                                )}
                                <Input
                                    label="Total Nitrogen Applied"
                                    labelPlacement="inside"
                                    size="sm"
                                    isReadOnly
                                    value={
                                        totalNitrogenApplied !== null
                                            ? `${totalNitrogenApplied} lbs/acre`
                                            : ""
                                    }
                                />
                            </div>
                            <div className="text-sm font-medium text-gray-700 m-2">
                                Applications
                            </div>
                            <div className="flex flex-col gap-2">
                                {planned_applications?.map(
                                    (_application, index) => (
                                        <Card>
                                            <CardBody>
                                                <div
                                                    key={index}
                                                    className="flex flex-row gap-1 items-center justify-center"
                                                >
                                                    <div className="flex flex-col gap-1 mx-1">
                                                        <p className="text-sm font-medium text-gray-700 text-center">
                                                            Plots
                                                        </p>
                                                        <Switch
                                                            size="sm"
                                                            isDisabled={
                                                                !userCanEditApplicationPlan ||
                                                                !editApplicationPlan
                                                            }
                                                            onValueChange={(
                                                                value,
                                                            ) => {
                                                                const updatedApplications =
                                                                    planned_applications.map(
                                                                        (
                                                                            application,
                                                                        ) => {
                                                                            return {
                                                                                ...application,
                                                                                establish_plots:
                                                                                    application.id ===
                                                                                    planned_applications[
                                                                                        index
                                                                                    ]
                                                                                        .id &&
                                                                                    value,
                                                                            };
                                                                        },
                                                                    );
                                                                applicationPlanForm.setValue(
                                                                    "planned_applications",
                                                                    updatedApplications,
                                                                );
                                                            }}
                                                            isSelected={
                                                                planned_applications[
                                                                    index
                                                                ]
                                                                    .establish_plots
                                                            }
                                                        />
                                                    </div>
                                                    <div className="flex flex-col w-full justify-between gap-1">
                                                        <div className="flex flex-row justify-between w-full gap-1">
                                                            <SelectInput
                                                                name={`planned_applications.${index}.target_crop_stage`}
                                                                label="Growth Stage"
                                                                labelPlacement="inside"
                                                                options={
                                                                    cropStageOptions
                                                                }
                                                                size="sm"
                                                                isDisabled={
                                                                    !userCanEditApplicationPlan ||
                                                                    !editApplicationPlan
                                                                }
                                                            />
                                                            <SelectInput
                                                                name={`planned_applications.${index}.method`}
                                                                label="Method"
                                                                labelPlacement="inside"
                                                                options={
                                                                    applicationMethodOptions
                                                                }
                                                                size="sm"
                                                                isDisabled={
                                                                    !userCanEditApplicationPlan ||
                                                                    !editApplicationPlan
                                                                }
                                                            />
                                                            <NumberInput
                                                                name={`planned_applications.${index}.application_cost`}
                                                                label="Application Cost"
                                                                labelPlacement="inside"
                                                                size="sm"
                                                                startContent={
                                                                    <div className="text-sm">
                                                                        $
                                                                    </div>
                                                                }
                                                                isReadOnly={
                                                                    !userCanEditApplicationPlan ||
                                                                    !editApplicationPlan
                                                                }
                                                            />
                                                        </div>
                                                        <div className="flex flex-row justify-between w-full gap-1 items-center">
                                                            <SectionedAutoCompleteInput
                                                                name={`planned_applications.${index}.fertilizer_product`}
                                                                label="Product"
                                                                labelPlacement="inside"
                                                                groups={
                                                                    fertilizerOptions
                                                                }
                                                                size="sm"
                                                                isDisabled={
                                                                    !userCanEditApplicationPlan ||
                                                                    !editApplicationPlan
                                                                }
                                                            />
                                                            <NumberInput
                                                                name={`planned_applications.${index}.user_rate`}
                                                                label="Target Rate"
                                                                labelPlacement="inside"
                                                                placeholder="Use Model Rate"
                                                                size="sm"
                                                                isReadOnly={
                                                                    !userCanEditApplicationPlan ||
                                                                    !editApplicationPlan
                                                                }
                                                            />
                                                            <NumberInput
                                                                name={`planned_applications.${index}.model_rate`}
                                                                label="All-N Rate"
                                                                labelPlacement="inside"
                                                                size="sm"
                                                                isDisabled
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            isIconOnly
                                                            isDisabled={
                                                                Array.from(
                                                                    selectedApplicationPlanIds,
                                                                ).length === 0
                                                            }
                                                            onPress={() => {
                                                                const updatedApplications =
                                                                    planned_applications.filter(
                                                                        (
                                                                            _,
                                                                            appIndex,
                                                                        ) =>
                                                                            appIndex !==
                                                                            index,
                                                                    );
                                                                applicationPlanForm.setValue(
                                                                    "planned_applications",
                                                                    updatedApplications,
                                                                );
                                                            }}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faXmark}
                                                            />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    ),
                                )}
                            </div>
                            <div className="flex flex-row w-full justify-center gap-1 mt-2">
                                <Button
                                    size="sm"
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faPlus}
                                            className="mr-1"
                                        />
                                    }
                                    isDisabled={
                                        !userCanEditApplicationPlan ||
                                        !editApplicationPlan
                                    }
                                    onPress={() => {
                                        applicationPlanForm.setValue(
                                            "planned_applications",
                                            [
                                                ...(applicationPlanForm.getValues(
                                                    "planned_applications",
                                                ) ?? []),
                                                {
                                                    id: null,
                                                    target_crop_stage:
                                                        cropStageOptions[0].id,
                                                    method: ApplicationMethod.Broadcast,
                                                    user_rate: null,
                                                    application_cost: null,
                                                    fertilizer_product: "2",
                                                    establish_plots: false,
                                                },
                                            ],
                                        );
                                    }}
                                >
                                    Add Application
                                </Button>
                            </div>
                            <div className="flex flex-row w-full justify-end gap-2 items-center mt-2">
                                <>
                                    {applicationPlanId &&
                                        !editApplicationPlan && (
                                            <Button
                                                startContent={
                                                    <FontAwesomeIcon
                                                        icon={faClone}
                                                        className="mr-1"
                                                    />
                                                }
                                                size="sm"
                                                onPress={() => {
                                                    const selectedKeys =
                                                        Array.from(
                                                            selectedApplicationPlanIds,
                                                        );
                                                    const displayKey =
                                                        selectedKeys[
                                                        selectedKeys.length -
                                                        1
                                                        ];
                                                    const displayPlan =
                                                        applicationPlansOptions.find(
                                                            (plan) =>
                                                                plan.id &&
                                                                plan.id.toString() ===
                                                                displayKey,
                                                        );
                                                    if (displayPlan) {
                                                        const newPlanData = {
                                                            ...displayPlan,
                                                            id: null,
                                                            name: `${displayPlan.name} (Copy)`,
                                                            organization:
                                                                userOrganizationId,
                                                        };
                                                        saveApplicationPlanMutation.mutate(
                                                            {
                                                                url: `/application_plans/`,
                                                                method: "POST",
                                                                data: newPlanData,
                                                            },
                                                            {
                                                                onSuccess: (
                                                                    newPlan,
                                                                ) => {
                                                                    applicationPlansRequest
                                                                        .refetch()
                                                                        .then(
                                                                            () => {
                                                                                setSelectedApplicationPlanIds(
                                                                                    new Set(
                                                                                        [
                                                                                            newPlan.id!.toString(),
                                                                                        ],
                                                                                    ),
                                                                                );
                                                                                setEditApplicationPlan(
                                                                                    true,
                                                                                );
                                                                            },
                                                                        );
                                                                },
                                                            },
                                                        );
                                                    }
                                                }}
                                            >
                                                Copy
                                            </Button>
                                        )}
                                    {editApplicationPlan ? (
                                        <Button
                                            startContent={
                                                <FontAwesomeIcon
                                                    icon={faCheckCircle}
                                                    className="mr-1"
                                                />
                                            }
                                            color="primary"
                                            size="sm"
                                            isDisabled={
                                                !userCanEditApplicationPlan
                                            }
                                            isLoading={
                                                saveApplicationPlanMutation.isPending
                                            }
                                            onPress={() => {
                                                applicationPlanForm.trigger();
                                                applicationPlanForm.handleSubmit(
                                                    handleSaveApplicationPlan,
                                                )();
                                            }}
                                        >
                                            Save Application Plan
                                        </Button>
                                    ) : (
                                        <Button
                                            size="sm"
                                            isDisabled={
                                                !userCanEditApplicationPlan
                                            }
                                            onPress={() => {
                                                setEditApplicationPlan(true);
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faPenToSquare}
                                            />
                                            Edit
                                        </Button>
                                    )}
                                </>
                                {setPrimaryPlan && !editApplicationPlan && (
                                    <ConfirmPrimaryPlan
                                        fields={fields}
                                        plantingPlan={plantingPlan}
                                        applicationPlan={applicationPlan}
                                        cropPlans={cropPlansRequest.data}
                                        buttonSize="sm"
                                    />
                                )}
                            </div>
                        </form>
                    </FormProvider>
                </div>
            </div>
        </>
    );
}
